// features/automationScripts/utils/labels.js
//
// Turning the identifiers the executor sends into something a person reads.
// The stream is full of them — step aliases, request aliases, field paths —
// and printing them raw is what makes a run view look like a log file.

const ACRONYM = /^[A-Z0-9]{2,5}$/;

/**
 * An identifier as a person would say it:
 *   "statusCode"       → "Status code"
 *   "positive_request" → "Positive request"
 *   "Request1"         → "Request 1"
 *   "URL"              → "URL"       (left alone — an acronym is not a word)
 *
 * Anything with no word boundaries to find comes back as it arrived.
 */
export const humaniseIdentifier = (raw) => {
  if (raw == null || raw === '') return '';

  const tokens = String(raw)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')  // camelCase → camel Case
    .replace(/([A-Za-z])(\d)/g, '$1 $2')     // Request1  → Request 1
    .split(/[\s_-]+/)
    .filter(Boolean);

  if (!tokens.length) return String(raw);

  return tokens
    .map((token, i) => {
      if (ACRONYM.test(token)) return token;
      const lower = token.toLowerCase();
      return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    })
    .join(' ');
};

const STEP_ALIAS = /^step(\d+)(?:\.(.+))?$/i;

/**
 * Split a stream alias into its step number and its request part.
 * "step1" → { stepNumber: 1, requestAlias: null }
 * "step1.positive_request" → { stepNumber: 1, requestAlias: 'positive_request' }
 */
export const parseStepAlias = (alias) => {
  const match = STEP_ALIAS.exec(String(alias ?? '').trim());
  if (!match) return { stepNumber: null, requestAlias: null };
  return { stepNumber: Number(match[1]), requestAlias: match[2] || null };
};

/** "step1" → "Step 1". An alias that is not a step alias comes back unchanged. */
export const formatStepLabel = (alias) => {
  const { stepNumber } = parseStepAlias(alias);
  return stepNumber == null ? String(alias ?? '') : `Step ${stepNumber}`;
};

/**
 * The request within a step, named readably:
 * "step1.Request1" → "Request 1", "step1.positive_request" → "Positive request".
 * Empty when the alias names no request.
 */
export const formatRequestLabel = (alias) => {
  const { requestAlias } = parseStepAlias(alias);
  return requestAlias ? humaniseIdentifier(requestAlias) : '';
};

/**
 * How a permutation is named everywhere a run is shown, matching the executor's
 * HTML/PDF report: "Permutation 1: Variation check 1", or just "Permutation 1"
 * when its data-source row has no description.
 */
export const permutationLabel = (index, description) => {
  const text = description == null ? '' : String(description).trim();
  return `Permutation ${index}${text ? `: ${text}` : ''}`;
};
